import React from 'react';
import { Skeleton } from './ui/skeleton';

/**
 * Reusable skeleton blocks for loading states.
 *
 * Variants:
 *  - <KpiSkeleton />       4 KPI cards in a row
 *  - <TableSkeleton rows={N} /> table rows placeholder
 *  - <ListSkeleton items={N} />  vertical list placeholder
 *  - <CardSkeleton />      single card placeholder
 */

export const KpiSkeleton = ({ count = 4, testId = 'kpi-skeleton' }) => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
    data-testid={testId}
  >
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3"
        style={{ animationDelay: `${i * 60}ms` }}
      >
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-2 w-16" />
      </div>
    ))}
  </div>
);

export const TableSkeleton = ({ rows = 5, cols = 4, testId = 'table-skeleton' }) => (
  <div
    className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden"
    data-testid={testId}
  >
    <div className="border-b border-zinc-800 p-3 flex gap-4">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={`h-${i}`} className="h-3 flex-1" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div
        key={`r-${r}`}
        className="border-b border-zinc-800/60 p-3 flex gap-4 last:border-b-0"
      >
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={`c-${r}-${c}`} className="h-4 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

export const ListSkeleton = ({ items = 5, testId = 'list-skeleton' }) => (
  <div className="space-y-2" data-testid={testId}>
    {Array.from({ length: items }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/40 border border-zinc-800"
      >
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-2.5 w-1/2" />
        </div>
        <Skeleton className="h-6 w-16" />
      </div>
    ))}
  </div>
);

export const CardSkeleton = ({ testId = 'card-skeleton' }) => (
  <div
    className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-3"
    data-testid={testId}
  >
    <Skeleton className="h-4 w-1/3" />
    <Skeleton className="h-3 w-2/3" />
    <Skeleton className="h-3 w-3/4" />
    <div className="pt-2 flex gap-2">
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-8 w-20" />
    </div>
  </div>
);
